import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAdminWrapped = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const statsQuery = useQuery({
    queryKey: ["admin-wrapped-stats"],
    queryFn: async () => {
      // First fetch wrapped stats
      const { data: wrappedStats, error: statsError } = await supabase
        .from("wrapped_stats")
        .select("id, user_id, year, total_events, updated_at, slide_views, share_clicks, total_views, total_shares, last_viewed_at")
        .order("updated_at", { ascending: false });

      if (statsError) throw statsError;
      if (!wrappedStats?.length) return [];

      // Fetch profiles for all users
      const userIds = [...new Set(wrappedStats.map(s => s.user_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Create a map for quick lookup
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Merge data
      return wrappedStats.map(stat => ({
        ...stat,
        first_name: profileMap.get(stat.user_id)?.first_name || null,
        last_name: profileMap.get(stat.user_id)?.last_name || null,
        email: profileMap.get(stat.user_id)?.email || null,
      }));
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (year: number) => {
      setIsGenerating(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("generate-wrapped-stats", {
        body: { year },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: "Wrapped stats generated",
        description: `Processed ${data.usersProcessed} users${data.errors?.length ? ` with ${data.errors.length} errors` : ""}`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin-wrapped-stats"] });
    },
    onError: (error) => {
      toast({
        title: "Error generating stats",
        description: String(error),
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsGenerating(false);
    },
  });

  return {
    stats: statsQuery.data,
    isLoading: statsQuery.isLoading,
    isGenerating,
    generateStats: generateMutation.mutate,
  };
};
